import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { useCompanyId } from "@/context/CompanyContext";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import type {
  MissionControlEventSubscription,
  V2MissionCompletedPayload,
  V2MissionCreatedPayload,
  V2MissionStageChangedPayload,
} from "@/context/MissionControlWebSocketContext";
import { clientsKeys } from "./keys";
import {
  applyMissionStageChangedToMissionDetailCache,
  invalidateClientMissions,
  patchMissionFieldsInClientList,
  patchMissionInClientList,
  taskBelongsToFaClientScope,
} from "./patches";

export interface UseClientMissionsOptions {
  faClientId: string;
  enabled?: boolean;
}

/** WebSocket handlers that patch or invalidate the cached client missions list. */
export function createClientMissionsWsSubscription(
  queryClient: QueryClient,
  faClientId: string,
): MissionControlEventSubscription {
  return {
    onMissionCreated: (payload: V2MissionCreatedPayload) => {
      if (payload.mission) {
        patchMissionInClientList(queryClient, faClientId, payload.mission);
        return;
      }
      const belongsViaEntities = payload.entities.some(
        (entity) =>
          (entity.entity_type === "fa_client" || entity.entity_type === "client") &&
          entity.entity_id === faClientId,
      );
      if (
        belongsViaEntities ||
        taskBelongsToFaClientScope(queryClient, faClientId, {
          mission_id: payload.mission_id,
        })
      ) {
        invalidateClientMissions(queryClient, faClientId);
      }
    },
    onMissionStageChanged: (payload: V2MissionStageChangedPayload) => {
      if (
        !taskBelongsToFaClientScope(queryClient, faClientId, {
          mission_id: payload.mission_id,
        })
      ) {
        return;
      }
      applyMissionStageChangedToMissionDetailCache(queryClient, payload);
      if (payload.mission) {
        patchMissionInClientList(queryClient, faClientId, payload.mission);
        return;
      }
      patchMissionFieldsInClientList(queryClient, faClientId, payload.mission_id, {
        current_stage: payload.new_stage,
        updated_at: new Date().toISOString(),
      });
      invalidateClientMissions(queryClient, faClientId);
    },
    onMissionCompleted: (payload: V2MissionCompletedPayload) => {
      if (
        !taskBelongsToFaClientScope(queryClient, faClientId, {
          mission_id: payload.mission_id,
        })
      ) {
        return;
      }
      if (payload.mission) {
        patchMissionInClientList(queryClient, faClientId, payload.mission);
        return;
      }
      patchMissionFieldsInClientList(queryClient, faClientId, payload.mission_id, {
        status: payload.status,
      });
    },
  };
}

/** Missions scoped to a single fa_client entity. */
export function useClientMissions({
  faClientId,
  enabled: enabledOption = true,
}: UseClientMissionsOptions) {
  const isWindowLive = useWindowLive();
  const companyId = useCompanyId();
  const queryEnabled = Boolean(faClientId) && enabledOption && isWindowLive;

  const query = useQuery({
    queryKey: clientsKeys.missions(faClientId, companyId ?? undefined),
    enabled: queryEnabled,
    refetchOnWindowFocus: queryEnabled,
    queryFn: async () => {
      const page = await AgenticV2API.listMissions({ fa_client_id: faClientId });
      return page.items;
    },
  });

  return {
    missions: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
    refresh: () => void query.refetch(),
  };
}

/** QueryClient accessor for client missions cache patches. */
export function useClientMissionsQueryClient() {
  return useQueryClient();
}
